// Enhanced SearchPage.duck.js with improved combined search and data flow
import { storableError } from '../../util/errors';
import { convertUnitToSubUnit, unitDivisor } from '../../util/currency';
import {
  parseDateFromISO8601,
  getExclusiveEndDate,
  addTime,
  subtractTime,
  daysBetween,
  getStartOf,
} from '../../util/dates';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { constructQueryParamName, isOriginInUse, isStockInUse } from '../../util/search';
import { parse } from '../../util/urlHelpers';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { fetchDynamoDBListings, filterDynamoDBListings } from '../../util/dynamodbService';
import { fetchRentalTideListings } from '../../util/rentaltideService';

import config from '../../config/config';

const RESULT_PAGE_SIZE = 96;

// ================ Action types ================ //
export const SEARCH_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_LISTINGS_REQUEST';
export const SEARCH_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_LISTINGS_SUCCESS';
export const SEARCH_LISTINGS_ERROR = 'app/SearchPage/SEARCH_LISTINGS_ERROR';

export const SEARCH_MAP_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_MAP_LISTINGS_REQUEST';
export const SEARCH_MAP_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_MAP_LISTINGS_SUCCESS';
export const SEARCH_MAP_LISTINGS_ERROR = 'app/SearchPage/SEARCH_MAP_LISTINGS_ERROR';

export const SEARCH_MAP_SET_ACTIVE_LISTING = 'app/SearchPage/SEARCH_MAP_SET_ACTIVE_LISTING';

export const SHOW_LISTING_REQUEST = 'app/ListingPage/SHOW_LISTING_REQUEST';
export const SHOW_LISTING_ERROR = 'app/ListingPage/SHOW_LISTING_ERROR';

// DynamoDB action types
export const FETCH_DYNAMODB_LISTINGS_REQUEST = 'app/SearchPage/FETCH_DYNAMODB_LISTINGS_REQUEST';
export const FETCH_DYNAMODB_LISTINGS_SUCCESS = 'app/SearchPage/FETCH_DYNAMODB_LISTINGS_SUCCESS';
export const FETCH_DYNAMODB_LISTINGS_ERROR = 'app/SearchPage/FETCH_DYNAMODB_LISTINGS_ERROR';

// RentalTide action types
export const FETCH_RENTALTIDE_LISTINGS_REQUEST = 'app/SearchPage/FETCH_RENTALTIDE_LISTINGS_REQUEST';
export const FETCH_RENTALTIDE_LISTINGS_SUCCESS = 'app/SearchPage/FETCH_RENTALTIDE_LISTINGS_SUCCESS';
export const FETCH_RENTALTIDE_LISTINGS_ERROR = 'app/SearchPage/FETCH_RENTALTIDE_LISTINGS_ERROR';

// Combined search action types
export const SEARCH_ALL_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_ALL_LISTINGS_REQUEST';
export const SEARCH_ALL_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_ALL_LISTINGS_SUCCESS';
export const SEARCH_ALL_LISTINGS_ERROR = 'app/SearchPage/SEARCH_ALL_LISTINGS_ERROR';

// ================ Reducer ================ //
const initialState = {
  pagination: null,
  searchParams: null,
  searchInProgress: false,
  searchListingsError: null,
  currentPageResultIds: [],

  // DynamoDB state
  dynamoDBListings: [],
  dynamoDBSearchInProgress: false,
  dynamoDBSearchError: null,
  dynamoDBMeta: {
    totalItems: 0,
    totalPages: 0,
    page: 1,
    perPage: 0,
  },

  // RentalTide state
  rentalTideListings: [],
  rentalTideSearchInProgress: false,
  rentalTideSearchError: null,
  rentalTideMeta: {
    totalItems: 0,
    totalPages: 0,
    page: 1,
    perPage: 0,
  },

  // Combined search state
  combinedSearchInProgress: false,
  combinedSearchError: null,
  lastSearchTimestamp: null,

  // Map state
  activeListingId: null,
  searchMapListingIds: [],
};

const resultIds = (data, enableHold) =>
  data.data
    .filter(l => {
      if (enableHold) {
        return l.attributes.publicData.WR;
      }
      return (
        l.attributes.publicData === undefined ||
        l.attributes.publicData === null ||
        l.attributes.publicData.WR === true ||
        l.attributes.publicData.WR === undefined ||
        l.attributes.publicData.WR === null
      );
    })
    .map(l => l.id);

const searchPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;

  switch (type) {
    case SEARCH_ALL_LISTINGS_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        combinedSearchInProgress: true,
        searchInProgress: true,
        dynamoDBSearchInProgress: true,
        rentalTideSearchInProgress: true,
        searchListingsError: null,
        dynamoDBSearchError: null,
        rentalTideSearchError: null,
        combinedSearchError: null,
        lastSearchTimestamp: Date.now(),
      };

    case SEARCH_ALL_LISTINGS_SUCCESS:
      return {
        ...state,
        combinedSearchInProgress: false,
        lastSearchTimestamp: Date.now(),
      };

    case SEARCH_ALL_LISTINGS_ERROR:
      return {
        ...state,
        combinedSearchInProgress: false,
        combinedSearchError: payload,
        lastSearchTimestamp: Date.now(),
      };

    case SEARCH_LISTINGS_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        searchInProgress: true,
        searchMapListingIds: [],
        searchListingsError: null,
      };

    case SEARCH_LISTINGS_SUCCESS:
      const sharetribeIds = resultIds(payload.data, config.enableHold);
      return {
        ...state,
        currentPageResultIds: sharetribeIds,
        pagination: payload.data.meta,
        searchInProgress: false,
      };

    case SEARCH_LISTINGS_ERROR:
      console.error('Sharetribe search error:', payload);
      return {
        ...state,
        searchInProgress: false,
        searchListingsError: payload,
      };

    case FETCH_DYNAMODB_LISTINGS_REQUEST:
      return {
        ...state,
        dynamoDBSearchInProgress: true,
        dynamoDBSearchError: null,
      };

    case FETCH_DYNAMODB_LISTINGS_SUCCESS:
      return {
        ...state,
        dynamoDBListings: payload.listings,
        dynamoDBMeta: payload.meta || state.dynamoDBMeta,
        dynamoDBSearchInProgress: false,
      };

    case FETCH_DYNAMODB_LISTINGS_ERROR:
      console.error('❌ DynamoDB search error:', payload);
      return {
        ...state,
        dynamoDBSearchInProgress: false,
        dynamoDBSearchError: payload,
        dynamoDBListings: [],
        dynamoDBMeta: initialState.dynamoDBMeta,
      };

    case FETCH_RENTALTIDE_LISTINGS_REQUEST:
      return {
        ...state,
        rentalTideSearchInProgress: true,
        rentalTideSearchError: null,
      };

    case FETCH_RENTALTIDE_LISTINGS_SUCCESS:
      return {
        ...state,
        rentalTideListings: payload.listings,
        rentalTideMeta: payload.meta || state.rentalTideMeta,
        rentalTideSearchInProgress: false,
      };

    case FETCH_RENTALTIDE_LISTINGS_ERROR:
      console.error('RentalTide search error:', payload);
      return {
        ...state,
        rentalTideSearchInProgress: false,
        rentalTideSearchError: payload,
        rentalTideListings: [],
        rentalTideMeta: initialState.rentalTideMeta,
      };

    case SEARCH_MAP_SET_ACTIVE_LISTING:
      return {
        ...state,
        activeListingId: payload,
      };

    case SEARCH_MAP_LISTINGS_SUCCESS:
      return {
        ...state,
        searchMapListingIds: payload.searchMapListingIds,
      };

    default:
      return state;
  }
};

export default searchPageReducer;

// ================ Action creators ================ //
export const searchAllListingsRequest = searchParams => ({
  type: SEARCH_ALL_LISTINGS_REQUEST,
  payload: { searchParams },
});

export const searchAllListingsSuccess = (sharetribeResponse, dynamoDBResponse) => ({
  type: SEARCH_ALL_LISTINGS_SUCCESS,
  payload: { sharetribeResponse, dynamoDBResponse },
});

export const searchAllListingsError = e => ({
  type: SEARCH_ALL_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const searchListingsRequest = searchParams => ({
  type: SEARCH_LISTINGS_REQUEST,
  payload: { searchParams },
});

export const searchListingsSuccess = response => ({
  type: SEARCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const searchListingsError = e => ({
  type: SEARCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const fetchDynamoDBListingsRequest = () => ({
  type: FETCH_DYNAMODB_LISTINGS_REQUEST,
});

export const fetchDynamoDBListingsSuccess = (listings, meta) => ({
  type: FETCH_DYNAMODB_LISTINGS_SUCCESS,
  payload: { listings, meta },
});

export const fetchDynamoDBListingsError = e => ({
  type: FETCH_DYNAMODB_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const fetchRentalTideListingsRequest = () => ({
  type: FETCH_RENTALTIDE_LISTINGS_REQUEST,
});

export const fetchRentalTideListingsSuccess = (listings, meta) => ({
  type: FETCH_RENTALTIDE_LISTINGS_SUCCESS,
  payload: { listings, meta },
});

export const fetchRentalTideListingsError = e => ({
  type: FETCH_RENTALTIDE_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const showListingRequest = id => ({
  type: SHOW_LISTING_REQUEST,
  payload: { id },
});

export const showListingError = e => ({
  type: SHOW_LISTING_ERROR,
  error: true,
  payload: e,
});

export const setActiveListing = listingId => ({
  type: SEARCH_MAP_SET_ACTIVE_LISTING,
  payload: listingId,
});

// ================ Thunk actions ================ //

/**
 * Enhanced combined search that properly coordinates both data sources
 */
export const searchAllListings = (searchParams, config) => async (dispatch, getState, sdk) => {
  // Dispatch the combined search request
  dispatch(searchAllListingsRequest(searchParams));

  let sharetribeResponse = null;
  let dynamoDBResponse = null;
  let rentalTideResponse = null;
  let hasErrors = false;

  try {
    // Sharetribe search
    const sharetribePromise = dispatch(searchListings(searchParams, config))
      .then(response => {
        sharetribeResponse = response;
        return response;
      })
      .catch(error => {
        hasErrors = true;
        return null;
      });

    // DynamoDB search
    const dynamoDBPromise = dispatch(searchDynamoDBListings(searchParams))
      .then(response => {
        dynamoDBResponse = response;
        return response;
      })
      .catch(error => {
        console.error('DynamoDB search failed:', error);
        hasErrors = true;
        return null;
      });

    // RentalTide search
    const rentalTidePromise = dispatch(searchRentalTideListings(searchParams))
      .then(response => {
        rentalTideResponse = response;
        return response;
      })
      .catch(error => {
        console.error('RentalTide search failed:', error);
        hasErrors = true;
        return null;
      });

    // Wait for all three to complete
    await Promise.allSettled([sharetribePromise, dynamoDBPromise, rentalTidePromise]);

    // Dispatch success even if one source failed
    dispatch(searchAllListingsSuccess(sharetribeResponse, dynamoDBResponse));

    return {
      sharetribe: sharetribeResponse,
      dynamodb: dynamoDBResponse,
      rentaltide: rentalTideResponse,
      success: true,
      hasErrors,
    };
  } catch (error) {
    dispatch(searchAllListingsError(storableError(error)));

    return {
      sharetribe: sharetribeResponse,
      dynamodb: dynamoDBResponse,
      rentaltide: rentalTideResponse,
      success: false,
      error,
    };
  }
};

/**
 * Standalone DynamoDB search action
 */
export const searchDynamoDBListings = searchParams => async (dispatch, getState, sdk) => {
  dispatch(fetchDynamoDBListingsRequest());

  try {
    const response = await fetchDynamoDBListings(searchParams);

    const listings = response.data || [];
    const meta = response.meta || { totalItems: 0, totalPages: 0, page: 1, perPage: 0 };

    dispatch(fetchDynamoDBListingsSuccess(listings, meta));
    return response;
  } catch (error) {
    dispatch(fetchDynamoDBListingsError(storableError(error)));
    throw error;
  }
};

/**
 * Standalone RentalTide search action
 */
export const searchRentalTideListings = searchParams => async (dispatch, getState, sdk) => {
  dispatch(fetchRentalTideListingsRequest());

  try {
    const response = await fetchRentalTideListings(searchParams);

    const listings = response.data || [];
    const meta = response.meta || { totalItems: 0, totalPages: 0, page: 1, perPage: 0 };

    dispatch(fetchRentalTideListingsSuccess(listings, meta));
    return response;
  } catch (error) {
    dispatch(fetchRentalTideListingsError(storableError(error)));
    throw error;
  }
};

/**
 * Helper function for location variants
 */
const mergeMainAndLocationListing = (mainListing, locationListing) => {
  return {
    ...mainListing,
    id: locationListing.id,
    attributes: {
      ...mainListing.attributes,
      geolocation: locationListing.attributes.geolocation,
      publicData: {
        ...mainListing.attributes.publicData,
        location: locationListing.attributes.publicData.location,
        mainListingId: mainListing.id.uuid,
      },
    },
  };
};

/**
 * Process listings with location variants
 */
const listingsWithLocations = (response, searchParams) => async (dispatch, getState, sdk) => {
  const listings = response.data.data;

  // Only pass params valid for listings.show (not search-specific ones like bounds, page, sort)
  const showParams = {
    include: searchParams.include,
    'fields.listing': searchParams['fields.listing'],
    'fields.user': searchParams['fields.user'],
    'fields.image': searchParams['fields.image'],
    'limit.images': searchParams['limit.images'],
  };
  // Include image variant configs
  Object.keys(searchParams).forEach(key => {
    if (key.startsWith('imageVariant.')) {
      showParams[key] = searchParams[key];
    }
  });

  const modifiedListingsResponse = listings.map(async l => {
    try {
      const isLocationVariant = l.attributes.publicData.isLocationVariant;

      if (isLocationVariant) {
        const mainListingId = l.attributes.publicData.mainListingId;
        const mainListingResponse = await dispatch(showListing(mainListingId, showParams));

        const denormalisedMainListing = mainListingResponse?.data?.data;
        const currentListing = denormalisedMainListing
          ? mergeMainAndLocationListing(denormalisedMainListing, l)
          : l;

        return currentListing;
      }
      return l;
    } catch (error) {
      console.error('Error processing location variant listing:', error);
      return l;
    }
  });

  const modifiedListings = await Promise.all(modifiedListingsResponse);

  return {
    ...response,
    data: { ...response.data, data: modifiedListings },
  };
};

/**
 * Main Sharetribe search function
 */
export const searchListings = (searchParams, config) => (dispatch, getState, sdk) => {
  dispatch(searchListingsRequest(searchParams));

  try {
    const searchValidListingTypes = listingTypes => {
      return config?.listing?.enforceValidListingType
        ? {
            pub_listingType: listingTypes.map(l => l.listingType),
          }
        : {};
    };

    const omitInvalidCategoryParams = params => {
      const categoryConfig = config?.search?.defaultFilters?.find(
        f => f.schemaType === 'category'
      );
      const categories = config?.categoryConfiguration?.categories || [];
      const { key: prefix, scope } = categoryConfig || {};
      const categoryParamPrefix = constructQueryParamName(prefix, scope);

      const validURLParamForCategoryData = (prefix, categories, level, params) => {
        const levelKey = `${categoryParamPrefix}${level}`;
        const levelValue = params?.[levelKey];
        const foundCategory = categories.find(cat => cat.id === levelValue);
        const subcategories = foundCategory?.subcategories || [];
        return foundCategory && subcategories.length > 0
          ? {
              [levelKey]: levelValue,
              ...validURLParamForCategoryData(prefix, subcategories, level + 1, params),
            }
          : foundCategory
          ? { [levelKey]: levelValue }
          : {};
      };

      const categoryKeys = validURLParamForCategoryData(prefix, categories, 1, params);
      const nonCategoryKeys = Object.entries(params).reduce(
        (picked, [k, v]) => (k.startsWith(categoryParamPrefix) ? picked : { ...picked, [k]: v }),
        {}
      );

      return { ...nonCategoryKeys, ...categoryKeys };
    };

    const priceSearchParams = priceParam => {
      const inSubunits = value => convertUnitToSubUnit(value, unitDivisor(config.currency));
      const values = priceParam ? priceParam.split(',') : [];
      return priceParam && values.length === 2
        ? {
            price: [inSubunits(values[0]), inSubunits(values[1]) + 1].join(','),
          }
        : {};
    };

    const datesSearchParams = datesParam => {
      const searchTZ = 'Etc/UTC';
      const datesFilter = config?.search?.defaultFilters?.find(f => f.key === 'dates');
      const values = datesParam ? datesParam.split(',') : [];
      const hasValues = datesFilter && datesParam && values.length === 2;
      const { dateRangeMode, availability } = datesFilter || {};
      const isNightlyMode = dateRangeMode === 'night';
      const isEntireRangeAvailable = availability === 'time-full';

      const getProlongedStart = date => subtractTime(date, 14, 'hours', searchTZ);
      const getProlongedEnd = date => addTime(date, 12, 'hours', searchTZ);

      const startDate = hasValues ? parseDateFromISO8601(values[0], searchTZ) : null;
      const endRaw = hasValues ? parseDateFromISO8601(values[1], searchTZ) : null;
      const endDate =
        hasValues && isNightlyMode
          ? endRaw
          : hasValues
          ? getExclusiveEndDate(endRaw, searchTZ)
          : null;

      const today = getStartOf(new Date(), 'day', searchTZ);
      const possibleStartDate = subtractTime(today, 14, 'hours', searchTZ);
      const hasValidDates =
        hasValues &&
        startDate.getTime() >= possibleStartDate.getTime() &&
        startDate.getTime() <= endDate.getTime();

      const dayCount = isEntireRangeAvailable ? daysBetween(startDate, endDate) : 1;
      const day = 1440;
      const hour = 60;
      const minDuration = isEntireRangeAvailable ? dayCount * day - hour : hour;

      return hasValidDates
        ? {
            start: getProlongedStart(startDate),
            end: getProlongedEnd(endDate),
            availability: 'time-partial',
            minDuration,
          }
        : {};
    };

    const stockFilters = datesMaybe => {
      const hasDatesFilterInUse = Object.keys(datesMaybe).length > 0;
      return hasDatesFilterInUse ? {} : { minStock: 1, stockMode: 'match-undefined' };
    };

    const { perPage, price, dates, sort, mapSearch, ...restOfParams } = searchParams;
    const priceMaybe = priceSearchParams(price);
    const datesMaybe = datesSearchParams(dates);
    const stockMaybe = stockFilters(datesMaybe);
    const sortMaybe =
      sort === config?.search?.sortConfig?.relevanceKey ? {} : { sort };

    const fetchLocationVariant = config?.enableListingLocationVariant;
    const fetchLocationVariantMaybe = fetchLocationVariant
      ? {
          pub_isLocationVariant: restOfParams.bounds ? null : false,
        }
      : {};

    const params = {
      ...omitInvalidCategoryParams(restOfParams),
      ...priceMaybe,
      ...datesMaybe,
      ...stockMaybe,
      ...sortMaybe,
      ...searchValidListingTypes(config?.listing?.listingTypes || []),
      ...fetchLocationVariantMaybe,
      perPage,
    };

    return sdk.listings
      .query(params)
      .then(async response => {
        const listingFields = config?.listing?.listingFields;
        const listingsResponse = await dispatch(listingsWithLocations(response, searchParams));
        const sanitizeConfig = { listingFields };

        dispatch(addMarketplaceEntities(listingsResponse, sanitizeConfig));
        dispatch(searchListingsSuccess(listingsResponse));
        return listingsResponse;
      })
      .catch(e => {
        dispatch(searchListingsError(storableError(e)));
        throw e;
      });
  } catch (e) {
    // Catch synchronous errors (config access, param building) that would
    // otherwise leave searchInProgress stuck at true forever.
    console.error('searchListings error before SDK call:', e);
    dispatch(searchListingsError(storableError(e)));
    return Promise.reject(e);
  }
};

export const showListing = (listingId, searchParams) => (dispatch, getState, sdk) => {
  dispatch(showListingRequest(listingId));
  const show = sdk.listings.show({ ...searchParams, id: listingId });

  return show
    .then(data => {
      dispatch(addMarketplaceEntities(data));
      return data;
    })
    .catch(e => {
      dispatch(showListingError(storableError(e)));
      return null;
    });
};

/**
 * Load data function - main entry point for search page
 */
export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const {
    page = 1,
    address,
    origin,
    sort = config?.search?.sortConfig?.defaultOption || 'pub_promote',
    ...rest
  } = queryParams;

  const originMaybe = isOriginInUse(config) && origin ? { origin } : {};

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config?.layout?.listingImage || {};
  const aspectRatio = aspectHeight / aspectWidth;

  const searchParams = {
    ...rest,
    ...originMaybe,
    page,
    sort,
    perPage: RESULT_PAGE_SIZE,
    include: ['author', 'images'],
    'fields.listing': [
      'title',
      'createdAt',
      'geolocation',
      'price',
      'publicData.listingType',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      'publicData.promoteCommission',
      'publicData.location',
      'publicData.supercap',
      'publicData.ourPick',
      'publicData.instantBook',
      'publicData.promote',
      'publicData.promotedOn',
      'publicData.WR',
      'publicData.pickupEnabled',
      'publicData.shippingEnabled',
      'publicData.mainListingId',
      'publicData.isLocationVariant',
      'publicData.partner',
      'publicData.fareHarbor',
      'metadata.fareHarbor',
    ],
    'fields.user': [
      'profile.displayName',
      'profile.abbreviatedName',
      'profile.metadata.hostBookingCount',
      'profile.metadata.hostConversationCount',
      'profile.metadata.hostReviewCount',
      'profile.metadata.hostAverageRating',
    ],
    'fields.image': [
      'variants.scaled-small',
      'variants.scaled-medium',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
  };

  // Use the enhanced combined search
  return dispatch(searchAllListings(searchParams, config));
};
